/**
 * Rank badge for the gamification leaderboard.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import {
  LEADERBOARD_RANK_SIZE,
  LEADERBOARD_RANK_STYLES,
  type LeaderboardRankSize,
} from '@/components/gamification/gamification-configs';
import {
  isPodiumRank,
  rankAriaLabel,
  rankBadgeIcon,
} from '@/components/gamification/gamification-utils';

/** Props for {@link LeaderboardRankBadge}. */
export interface LeaderboardRankBadgeProps {
  rank: number;
  size?: LeaderboardRankSize;
  className?: string;
}

/** Trophy/medal for ranks 1–3; numeric `#N` badge otherwise. */
export const LeaderboardRankBadge: React.FC<LeaderboardRankBadgeProps> = ({
  rank,
  size = 'md',
  className,
}) => {
  const dims = LEADERBOARD_RANK_SIZE[size];
  const label = rankAriaLabel(rank);

  if (isPodiumRank(rank)) {
    const style = LEADERBOARD_RANK_STYLES[rank];
    const Icon = rankBadgeIcon(rank);
    return (
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full ring-2',
          dims.box,
          style.ring,
          style.bg,
          className,
        )}
        aria-label={label}
        title={label}
      >
        <Icon className={style.icon} size={dims.icon} aria-hidden />
      </span>
    );
  }

  return (
    <span
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full bg-muted font-bold text-foreground',
        dims.box,
        dims.text,
        className,
      )}
      aria-label={label}
    >
      #{rank}
    </span>
  );
};
